import { useMemo } from "react";
import {
	useRegisterCommands,
	type Command,
} from "@/commands/CommandRegistryContext";
import { CHEAT_COMMANDS } from "@/cheats/cheatCommandNames";
import {
	resolveFeatureIdForDockTarget,
} from "@/features/runtime/FeatureRegistry";
import type { TooltipPosition } from "@/integrations/captainup/CaptainUpTypes";
import type { EFFeatureDefinition } from "@/features/runtime/FeatureDefinition";
import type {
	EFFeatureDockTarget,
	EFFeatureId,
} from "@/features/runtime/FeatureTypes";
import { debugLog, debugWarn } from "@/utils";

function parseDockTargetPayload(payload: unknown): EFFeatureDockTarget | null {
	if (payload === "mission") {
		return "mission";
	}

	if (payload === "tournament") {
		return "tournament";
	}

	return null;
}

function parseTooltipPositionPayload(payload: unknown): TooltipPosition | null {
	if (
		!(payload === "top" ||
		payload === "bottom" ||
		payload === "left" ||
		payload === "right" ||
		payload === "top-left" ||
		payload === "top-right" ||
		payload === "bottom-left" ||
		payload === "bottom-right" ||
		payload === "none")
	) {
		debugWarn("[Cheats] invalid tooltip position payload", {
			payload,
		});
		return null;
	}

	return payload;
}

export type CaptainUpCheatCommandsProps = {
	activeFeatureId: EFFeatureId | null;
	featureRegistry: ReadonlyMap<EFFeatureId, EFFeatureDefinition>;
	openFeaturePopup: (featureId: EFFeatureId) => void;
	closeFeaturePopup: () => void;
	activateFeature: (featureId: EFFeatureId) => void;
	onSetTooltipPosition: (position: TooltipPosition) => void;
	onRetryBoot: () => void;
};

export function CaptainUpCheatCommands({
	activeFeatureId,
	featureRegistry,
	openFeaturePopup,
	closeFeaturePopup,
	activateFeature,
	onSetTooltipPosition,
	onRetryBoot,
}: CaptainUpCheatCommandsProps) {
	const commands = useMemo<Command[]>(() => {
		return [
			{
				name: CHEAT_COMMANDS.captainUp.setActiveDockWidget,
				execute: async (payload?: unknown) => {
					const dockTarget = parseDockTargetPayload(payload);

					if (!dockTarget) {
						debugWarn(
							"[Cheats] invalid payload for setActiveDockWidget",
							{
								payload,
							}
						);
						return;
					}

					const resolvedFeatureId = resolveFeatureIdForDockTarget(
						featureRegistry,
						dockTarget
					);

					if (!resolvedFeatureId) {
						debugWarn(
							"[Cheats] no feature is registered for requested dock target",
							{
								payload,
								dockTarget,
							}
						);
						return;
					}

					debugLog("[Cheats] activateFeature from dock cheat", {
						from: activeFeatureId,
						dockTarget,
						to: resolvedFeatureId,
					});

					activateFeature(resolvedFeatureId);
				},
			},
			{
				name: CHEAT_COMMANDS.captainUp.setTooltipPosition,
				execute: async (payload?: unknown) => {
					const position = parseTooltipPositionPayload(payload);

					if (!position) {
						debugWarn(
							"[Cheats] invalid payload for setTooltipPosition",
							{
								payload,
							}
						);
						return;
					}

					debugLog("[Cheats] setTooltipPosition", {
						position,
					});

					onSetTooltipPosition(position);
				},
			},
			{
				name: CHEAT_COMMANDS.captainUp.openPopup,
				execute: async () => {
					if (!activeFeatureId) {
						debugWarn(
							"[Cheats] openPopup ignored because no active feature is selected"
						);
						return;
					}

					debugLog("[Cheats] openFeaturePopup", {
						activeFeatureId,
					});

					openFeaturePopup(activeFeatureId);
				},
			},
			{
				name: CHEAT_COMMANDS.captainUp.closePopup,
				execute: async () => {
					debugLog("[Cheats] closeFeaturePopup", {
						activeFeatureId,
					});

					closeFeaturePopup();
				},
			},
			{
				name: CHEAT_COMMANDS.captainUp.retryBoot,
				execute: async () => {
					debugLog("[Cheats] retryBoot");
					onRetryBoot();
				},
			},
		];
	}, [
		activeFeatureId,
		activateFeature,
		closeFeaturePopup,
		featureRegistry,
		onSetTooltipPosition,
		onRetryBoot,
		openFeaturePopup,
	]);

	useRegisterCommands(commands);

	return null;
}

export default CaptainUpCheatCommands;
